const bootstrap = require('bootstrap');

export default {
    initialize(m: any, _this: { $emit: (arg0: string) => void }) {
        // Blur any focused descendant before Bootstrap marks the modal aria-hidden, otherwise the
        // browser warns "Blocked aria-hidden on an element because its descendant retained focus"
        // (e.g. clicking Save keeps focus on that button while hide.bs.modal fires).
        m.addEventListener('hide.bs.modal', () => {
            if (m.contains(document.activeElement)) {
                (document.activeElement as HTMLElement)?.blur();
            }
        });

        // hidden.bs.modal (not hide.bs.modal) fires only after Bootstrap finishes its hide
        // transition and removes the backdrop - destroying the DOM any earlier orphans the backdrop.
        m.addEventListener('hidden.bs.modal', () => {
            _this.$emit('hide');
        });

        return new bootstrap.Modal(m);
    },
    open(m: any) {
        m.show();
        this.resetModalDOMPosition(m._element);
    },
    hide(m: any) {
        m.hide();
    },
    resetModalDOMPosition(modalEl: HTMLElement) {
        // Since modal content is loaded (rendered) within the component it presents a challenge when launching
        // multiple modals. ie. Field rules modal and delete rule confirmation modal will display on top of each
        // other. So we move the just-opened modal (identified directly via the Bootstrap instance's own element,
        // rather than scanning the DOM for one "not yet moved") out to #main with an elevated z-index.
        if (!modalEl || modalEl.classList.contains('has-moved')) {
            return;
        }

        // A modal can be opened from a mounted() hook of a component that is one of several conditional
        // siblings in its parent's template (e.g. JumpStart alongside Announcements/Confirmation/etc. in
        // DataDiscrepancy.vue) - Vue may not have attached this element to the live document yet at that
        // point. Wait until it's actually connected before measuring/moving/z-indexing it.
        if (!modalEl.isConnected) {
            requestAnimationFrame(() => this.resetModalDOMPosition(modalEl));
            return;
        }

        const contentEl = window.document.getElementById('main') as HTMLElement;

        // Base the next z-index on the HIGHEST z-index currently assigned to an already-open
        // modal (not a fixed-doubling scheme) - this guarantees a newly-opened modal (e.g. a
        // confirmation dialog opened from within another modal, like JumpStart's "address
        // matches subject" message) always stacks strictly above every modal already open,
        // regardless of how many are open or in what order they were opened.
        const existingZIndexes = Array.from(window.document.querySelectorAll('.modal.has-moved'))
            .map((el) => parseInt((el as HTMLElement).style.zIndex || '0', 10))
            .filter((z) => !isNaN(z));
        const assignedZIndex = (existingZIndexes.length > 0 ? Math.max(...existingZIndexes) : 6000) + 2;

        const fragment = window.document.createDocumentFragment();
        modalEl.classList.add('has-moved');
        modalEl.style.zIndex = String(assignedZIndex);
        fragment.appendChild(modalEl);
        contentEl.appendChild(fragment);

        // Bootstrap can insert this modal's backdrop into the DOM asynchronously relative to
        // show() - querying for it synchronously here can grab a DIFFERENT (already-open)
        // modal's backdrop, or find nothing at all and leave this modal's real backdrop stuck
        // at Bootstrap's raw default z-index. Wait a frame so the backdrop is guaranteed to exist.
        requestAnimationFrame(() => {
            const backdrop = window.document.querySelector('.modal-backdrop:not(.has-new-z-index)');
            if (backdrop) {
                backdrop.classList.add('has-new-z-index');
                (backdrop as HTMLElement).style.zIndex = String(assignedZIndex - 1);
            }
        });
    },
    size(m: any, size: string) {
        const elDialog = m.getElementsByClassName('modal-dialog');

        switch (size) {
            case 'sm':
                elDialog[0].classList.add('modal-sm');
                break;
            case 'lg':
                elDialog[0].classList.add('modal-lg');
                break;
            case 'xl':
                elDialog[0].classList.add('modal-xl');
                break;
            case 'auto': {
                this.onAutoResize(m);
                window.addEventListener('resize', () => {
                    this.onAutoResize(m);
                });

                break;
            }
            case 'auto-w': {
                this.onAutoWResize(m);
                window.addEventListener('resize', () => {
                    this.onAutoWResize(m);
                });

                break;
            }
            case 'auto-h': {
                this.onAutoHResize(m);
                window.addEventListener('resize', () => {
                    this.onAutoHResize(m);
                });

                break;
            }
            case 'auto-mls': {
                this.onAutoMLSResize(m);
                window.addEventListener('resize', () => {
                    this.onAutoMLSResize(m);
                });

                break;
            }
            case '600':
                elDialog[0].classList.add('modal-600');
                break;
            default:
                break;
        }
    },
    onAutoResize(m: any) {
        const elDialog = m.getElementsByClassName('modal-dialog');
        const elDialogBody = m.getElementsByClassName('modal-body');
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        elDialog[0].style.cssText = `   height: ${Number(height) - 165}px !important; 
                                        max-width: ${Number(width) - 40}px !important;
                                        width: ${Number(width) - 40}px !important;`;

        elDialogBody[0].style.cssText = `height: ${Number(height) - 165}px !important; overflow: auto`;
    },
    onAutoWResize(m: any) {
        const elDialog = m.getElementsByClassName('modal-dialog');
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        elDialog[0].style.cssText = `   max-width: ${Number(width) - 40}px !important; 
                                        width: ${Number(width) - 40}px !important;`;
    },
    onAutoHResize(m: any) {
        const elDialog = m.getElementsByClassName('modal-dialog');
        const elDialogBody = m.getElementsByClassName('modal-body');
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        elDialog[0].style.cssText = `   height: ${Number(height) - 165}px !important;`;
        elDialogBody[0].style.cssText = `height: ${Number(height) - 165}px !important; overflow: auto`;
    },
    onAutoMLSResize(m: any) {
        const elDialog = m.getElementsByClassName('modal-dialog');
        const elIframe = m.getElementsByTagName('iframe');
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        elDialog[0].style.cssText = `   height: ${Number(height) - 125}px !important; 
                                        max-width: ${Number(width) - 40}px !important;
                                        width: ${Number(width) - 40}px !important;`;

        elIframe[0].style.cssText = `   height: ${Number(height) - 125}px !important`;
    }
};
